import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X, ShieldCheck } from "lucide-react";
import { teacherApi } from "@/lib/api";

/* In-app counterpart to the e-mail "new login alert". Polls `recent-login-alerts`
   once on mount and shows a dismissable banner above the dashboard content.
   The banner deep-links to /teacher/settings where the teacher can revoke
   sessions / trusted devices if the login isn't recognised. */
export default function NewLoginAlertBanner() {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    teacherApi.get("/auth/recent-login-alerts")
      .then(({ data }) => { if (mounted) setAlerts(data.alerts || []); })
      .catch(() => { /* silent — banner is non-essential UI */ });
    return () => { mounted = false; };
  }, []);

  if (dismissed || alerts.length === 0) return null;

  const top = alerts[0];
  const meta = top.metadata || {};
  const when = (() => {
    try { return new Date(top.ts).toLocaleString("pl"); }
    catch { return top.ts; }
  })();

  const acknowledge = async () => {
    setDismissed(true);  // optimistic dismiss
    try {
      await teacherApi.post("/auth/recent-login-alerts/acknowledge");
    } catch { /* it'll just show again on next fetch — non-critical */ }
  };

  return (
    <div className="brut border-2 border-[#0F172A] bg-[#FEF08A] rounded-md p-4 mb-6
                    flex flex-wrap gap-4 items-start"
         data-testid="new-login-alert-banner">
      <div className="brut bg-white border-2 border-[#0F172A] w-12 h-12
                      flex items-center justify-center rounded-md shrink-0">
        <AlertTriangle className="w-6 h-6" strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] tracking-[0.25em] uppercase font-black text-[#0F172A]">
          Wykryliśmy nowe logowanie
        </div>
        <div className="font-display font-black text-lg sm:text-xl leading-tight mt-1
                        text-[#0F172A]">
          {meta.country ? `Logowanie z kraju ${meta.country}` : "Nowa lokalizacja"}
          {meta.browser ? ` · ${meta.browser}` : ""}
        </div>
        <div className="text-xs sm:text-sm text-[#334155] mt-1 font-mono break-all">
          IP {meta.ip || "?"} · {when} · powód: {meta.reason || "—"}
        </div>
        <div className="text-xs sm:text-sm text-[#334155] mt-1.5">
          Jeśli to <b>byłeś Ty</b> — zignoruj. Jeśli <b>nie poznajesz</b> tej sesji,
          wyloguj wszystkie urządzenia i zmień hasło.
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
        <Link
          to="/teacher/settings"
          className="brut brut-hover bg-[#0F172A] text-white font-bold rounded-md
                     px-4 py-2 text-sm inline-flex items-center gap-1.5 h-10
                     border-2 border-[#0F172A]"
          data-testid="alert-go-to-settings-btn"
        >
          <ShieldCheck className="w-4 h-4" strokeWidth={2.5} />
          Sprawdź ustawienia
        </Link>
        <button
          onClick={acknowledge}
          className="brut brut-hover bg-white text-[#0F172A] font-bold rounded-md
                     px-3 py-2 text-sm inline-flex items-center gap-1.5 h-10
                     border-2 border-[#0F172A]"
          data-testid="alert-dismiss-btn"
          aria-label="Odrzuć alert"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
          To ja
        </button>
      </div>
    </div>
  );
}
